import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useWebSocket, useWebSocketSubscription } from '../../contexts/WebSocketContext';
import { TelemetryUpdate } from '@cbm/shared';

interface LineBMetrics {
  speed: number;
  temperature: number;
  pressure: number;
  vibration: number;
  efficiency: number;
  timestamp: string;
}

export const LineBVisualization: React.FC = () => {
  const [metrics, setMetrics] = useState<LineBMetrics[]>([]);
  const [status, setStatus] = useState<'waiting' | 'active'>('waiting');
  const { subscribeToMachine, unsubscribeFromMachine } = useWebSocket();

  useEffect(() => {
    subscribeToMachine('MACHINE-002');
    return () => unsubscribeFromMachine('MACHINE-002');
  }, [subscribeToMachine, unsubscribeFromMachine]);

  useWebSocketSubscription({
    onTelemetryUpdate: (data: TelemetryUpdate) => {
      if (data.payload.machineId === 'MACHINE-002') {
        const sample = data.payload.sample;
        const tags = sample.tags || [];
        const getTag = (name: string) => tags.find((t: any) => t.name === name)?.value || 0;
        
        const newMetric: LineBMetrics = {
          speed: getTag('line_speed'),
          temperature: getTag('temperature'),
          pressure: getTag('pressure'),
          vibration: getTag('vibration'),
          efficiency: getTag('efficiency'),
          timestamp: new Date(sample.timestamp).toLocaleTimeString()
        };
        
        setMetrics(prev => [...prev.slice(-50), newMetric]);
        setStatus('active');
      }
    }
  });

  const latest = metrics[metrics.length - 1];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Production Line B</h1>
          <p className="text-gray-600 mt-2">Machine ID: MACHINE-002</p>
        </div>

        {status === 'waiting' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800">⏳ Waiting for telemetry data...</p>
          </div>
        )}

        {latest && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <MetricCard label="Speed" value={latest.speed.toFixed(0)} unit="RPM" color="blue" />
            <MetricCard label="Temperature" value={latest.temperature.toFixed(1)} unit="°C" color="red" />
            <MetricCard label="Pressure" value={latest.pressure.toFixed(1)} unit="PSI" color="green" />
            <MetricCard label="Vibration" value={latest.vibration.toFixed(2)} unit="mm/s" color="purple" />
            <MetricCard label="Efficiency" value={latest.efficiency.toFixed(1)} unit="%" color="indigo" />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="Speed & Efficiency">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={metrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="speed" stroke="#3b82f6" name="Speed (RPM)" />
                <Line type="monotone" dataKey="efficiency" stroke="#8b5cf6" name="Efficiency (%)" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Temperature & Pressure">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={metrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="temperature" stroke="#ef4444" name="Temp (°C)" />
                <Line type="monotone" dataKey="pressure" stroke="#10b981" name="Pressure (PSI)" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Vibration">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={metrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="vibration" stroke="#a855f7" name="Vibration (mm/s)" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </div>
  );
};

const MetricCard: React.FC<{ label: string; value: string; unit: string; color: string }> = ({ label, value, unit, color }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  };

  return (
    <div className={`border rounded-lg p-4 ${colors[color as keyof typeof colors]}`}>
      <p className="text-sm font-medium opacity-75">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
      <p className="text-xs opacity-75 mt-1">{unit}</p>
    </div>
  );
};

const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
    {children}
  </div>
);
